import React, { useEffect, useState, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  User,
  Users,
  Shield,
  LogOut,
  ChevronDown,
  Edit,
  Building2,
  Calendar,
  Home,
  UserCheck,
  Menu,
  X,
  Sparkles,
  Microscope
} from 'lucide-react';
import type { RootState, AppDispatch } from '../../store';
import { clearAuth } from '../../store/slices/authSlice';
import { usePermissions } from '../../hooks/usePermissions';
import { prefetchWorklistWarm } from '../../hooks/useWorklistQueries';

export const Layout: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const isWorklistRoute =
    location.pathname === '/worklist' || location.pathname === '/dashboard/worklist';
  const isPatientsRoute =
    location.pathname === '/patients' || location.pathname === '/dashboard/patients';
  const isBiopsiasRoute =
    location.pathname === '/biopsias' || location.pathname === '/dashboard/biopsias';
  const isFullWidthRoute = isWorklistRoute || isPatientsRoute || isBiopsiasRoute;
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Add dashboard-page class to body for dashboard pages
    document.body.classList.add('dashboard-page');

    return () => {
      // Clean up - remove the class when leaving dashboard
      document.body.classList.remove('dashboard-page');
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    // Clear auth state and navigate to login
    dispatch(clearAuth());
    navigate('/auth/login');
  };

  const getUserRoleDisplay = () => {
    if (!user?.profile) return 'Usuario';

    const roleNames = {
      'superadmin': 'Superadministrador',
      'institution-admin': 'Admin Institución',
      'medico': 'Médico',
      'asistente': 'Asistente'
    };

    return roleNames[user.profile] || 'Usuario';
  };

  // Check navigation access permissions
  const canViewPatients = hasPermission('view_patients') || user?.profile === 'superadmin';
  const canViewWorklist = hasPermission('view_worklist') || user?.profile === 'medico' || user?.profile === 'institution-admin' || user?.profile === 'superadmin' || user?.profile === 'asistente';
  const canViewBiopsias = hasPermission('view_biopsias') || user?.profile === 'superadmin';

  // Check admin permissions - Superusers can access everything
  const canAccessUserManagement = hasPermission('manage_users') || user?.profile === 'superadmin';
  const canAccessInstitutionManagement = hasPermission('manage_institutions') || user?.profile === 'superadmin';
  const canAccessSystemSettings = hasPermission('system_config') || user?.profile === 'superadmin';
  const canAccessAIConfig =
    hasPermission('manage_ai_config') ||
    user?.profile === 'superadmin' ||
    user?.is_superuser === true;
  const canAccessInstitutionAdmin = user?.profile === 'institution-admin' || user?.profile === 'superadmin';

  return (
    <div className="min-h-screen bg-background-main">
      {/* Header */}
      <header className="bg-background-card shadow-gynhub-soft border-b border-border-light">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-6">
              <Link to="/dashboard" className="text-xl font-futura-medium text-brand-primary hover:text-brand-secondary transition-colors">
                GynHub
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-text-secondary hover:text-brand-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-primary"
              >
                <span className="sr-only">Abrir menú principal</span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" />
                ) : (
                  <Menu className="block h-6 w-6" />
                )}
              </button>

              {/* Main Navigation - Show based on permissions */}
              <nav className="hidden md:flex items-center space-x-6">
                {canViewPatients && (
                  <Link
                    to="/dashboard/patients"
                    className="flex items-center space-x-2 text-sm text-text-secondary hover:text-brand-primary transition-colors"
                  >
                    <UserCheck className="w-4 h-4" />
                    <span>Patients</span>
                  </Link>
                )}

                {canViewWorklist && (
                  <Link
                    to="/worklist"
                    className="flex items-center space-x-2 text-sm text-text-secondary hover:text-brand-primary transition-colors"
                    onMouseEnter={() =>
                      prefetchWorklistWarm(new Date().toISOString().split('T')[0]!)
                    }
                    onFocus={() =>
                      prefetchWorklistWarm(new Date().toISOString().split('T')[0]!)
                    }
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Worklist</span>
                  </Link>
                )}

                {canViewBiopsias && (
                  <Link
                    to="/biopsias"
                    className="flex items-center space-x-2 text-sm text-text-secondary hover:text-brand-primary transition-colors"
                  >
                    <Microscope className="w-4 h-4" />
                    <span>Biopsias</span>
                  </Link>
                )}

                <Link
                  to="/dashboard"
                  className="flex items-center space-x-2 text-sm text-text-secondary hover:text-brand-primary transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span>Dashboard</span>
                </Link>
              </nav>
            </div>

            <div className="flex items-center space-x-4">
              {user && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-sm text-text-primary hover:text-brand-primary transition-colors focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 rounded-md px-3 py-2"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-brand-primary text-white rounded-full">
                      <User className="w-4 h-4" />
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="font-medium">
                        {user.full_name || user.username}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {getUserRoleDisplay()}
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {/* User Info */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <div className="flex items-center justify-center w-10 h-10 bg-brand-primary text-white rounded-full">
                              <User className="w-5 h-5" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900">
                                {user.full_name || user.username}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                              <div className="text-xs text-brand-primary">
                                {getUserRoleDisplay()}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <User className="w-4 h-4 mr-3" />
                          Mi Perfil
                        </Link>

                        <Link
                          to="/profile/signature"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Edit className="w-4 h-4 mr-3" />
                          Gestión de Firmas
                        </Link>

                        {/* Admin Section - Show if user has any admin permissions */}
                        {(canAccessUserManagement || canAccessInstitutionManagement || canAccessSystemSettings || canAccessInstitutionAdmin || canAccessAIConfig) && (
                          <>
                            <div className="border-t border-gray-100 my-1"></div>
                            <div className="px-4 py-2">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Administración
                              </div>
                            </div>

                            {/* Superadmin features */}
                            {canAccessUserManagement && (
                              <Link
                                to="/admin/users"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Users className="w-4 h-4 mr-3" />
                                Gestión de Usuarios
                              </Link>
                            )}

                            {canAccessInstitutionManagement && (
                              <Link
                                to="/admin/institutions"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Building2 className="w-4 h-4 mr-3" />
                                Gestión de Instituciones
                              </Link>
                            )}

                            {canAccessSystemSettings && (
                              <Link
                                to="/admin/settings"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Shield className="w-4 h-4 mr-3" />
                                Configuración del Sistema
                              </Link>
                            )}

                            {/* AI Configuration - manage_ai_config or superadmin */}
                            {canAccessAIConfig && (
                              <Link
                                to="/admin/ai-config"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Sparkles className="w-4 h-4 mr-3" />
                                Configuración IA
                              </Link>
                            )}

                            {/* Worklist Setup - Available for institution-admin and superadmin */}
                            {(canAccessInstitutionAdmin || canAccessInstitutionManagement) && (
                              <Link
                                to="/admin/worklist-setup"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Calendar className="w-4 h-4 mr-3" />
                                Configuración de Worklist
                              </Link>
                            )}

                            {/* Institution admin features */}
                            {canAccessInstitutionAdmin && !canAccessInstitutionManagement && (
                              <Link
                                to="/admin/institution"
                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-primary transition-colors"
                                onClick={() => setIsUserMenuOpen(false)}
                              >
                                <Building2 className="w-4 h-4 mr-3" />
                                Mi Institución
                              </Link>
                            )}
                          </>
                        )}

                        {/* Settings and Logout */}
                        <div className="border-t border-gray-100 my-1"></div>

                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                        >
                          <LogOut className="w-4 h-4 mr-3" />
                          Cerrar Sesión
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden" ref={mobileMenuRef}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-background-card shadow-lg">
            {canViewPatients && (
              <Link
                to="/dashboard/patients"
                className="flex items-center px-3 py-2 text-base font-medium text-text-secondary hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <UserCheck className="w-5 h-5 mr-3" />
                Patients
              </Link>
            )}

            {canViewWorklist && (
              <Link
                to="/worklist"
                className="flex items-center px-3 py-2 text-base font-medium text-text-secondary hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
                onMouseEnter={() =>
                  prefetchWorklistWarm(new Date().toISOString().split('T')[0]!)
                }
                onFocus={() =>
                  prefetchWorklistWarm(new Date().toISOString().split('T')[0]!)
                }
              >
                <Calendar className="w-5 h-5 mr-3" />
                Worklist
              </Link>
            )}

            {canViewBiopsias && (
              <Link
                to="/biopsias"
                className="flex items-center px-3 py-2 text-base font-medium text-text-secondary hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Microscope className="w-5 h-5 mr-3" />
                Biopsias
              </Link>
            )}

            <Link
              to="/dashboard"
              className="flex items-center px-3 py-2 text-base font-medium text-text-secondary hover:text-brand-primary hover:bg-gray-50 rounded-md transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Home className="w-5 h-5 mr-3" />
              Dashboard
            </Link>
          </div>
        </div>
      )}

      {/* Main content */}
      <main
        className={`w-full min-w-0 py-4 sm:py-6 ${
          isFullWidthRoute ? 'px-2 sm:px-4 lg:px-6' : 'mx-auto px-4 sm:px-6 lg:px-8'
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
};
